/**
 * Full color palette for Atlas.
 *
 * - `accent`:    10-step Atlas blue scale
 * - `secondary`: 5-step Atlas teal scale
 * - `semantic`:  success/warning/danger/info
 * - `light` / `dark`: theme-specific surface + text tokens
 */

export const accent = {
  50: '#EDF6FB',
  100: '#DCECF5',
  200: '#B9D9EB',
  300: '#91C4DE',
  400: '#66ADD1',
  500: '#317BA5',
  600: '#2A6B94',
  700: '#205676',
  800: '#19445E',
  900: '#112F42',
} as const;

export const secondary = {
  100: '#DFF4F0',
  300: '#92D3CA',
  500: '#299B96',
  600: '#207E79',
  700: '#17615D',
} as const;

export const semantic = {
  success: '#16A34A',
  successSoftLight: '#DCFCE7',
  successSoftDark: '#052E16',
  warning: '#F59E0B',
  warningSoftLight: '#FEF3C7',
  warningSoftDark: '#451A03',
  danger: '#DC2626',
  dangerSoftLight: '#FEE2E2',
  dangerSoftDark: '#450A0A',
  info: '#317BA5',
} as const;

// ── Surface + text, per theme ──────────────────────────────────────
//
// Generated FROM `tokens.css` by hand-transcription, not the other way
// round — `tokens.css` is what actually renders, so it is authoritative.
// Until 2026-08-07 this drifted badly: `dark` still held the pre-repaint
// neutral-gray values (`bgApp`/`bgSurface`/`bgElevated`/`bgInset`/`border`/
// `borderStrong`/`textPrimary`/`textSecondary`/`textMuted`/`textInverse`),
// and BOTH themes' `dotColor` were stale, from before `tokens.css` got its
// "warm near-black" repaint (see its own header comment). Nothing imported these dark values, which is
// exactly why the drift went unnoticed for as long as it did — see the
// design-tokens.test.ts parity test below, added so this cannot happen
// silently again.
export const light = {
  bgApp: '#f2f4f9',
  bgSurface: '#ffffff',
  bgElevated: '#e8ebf5',
  bgInset: '#e8ebf5',
  border: 'rgb(77 91 134 / .14)',
  borderStrong: 'rgb(77 91 134 / .25)',
  textPrimary: '#23283b',
  textSecondary: '#576076',
  textMuted: '#6a738a',
  textInverse: '#ffffff',
  /** White over the darker brand accent surfaces in both themes. */
  textOnBrand: '#ffffff',
  /** Mirrors `--dot-color` under `:root` in tokens.css. */
  dotColor: 'rgba(49, 123, 165, 0.18)',
  /** Text-weight variants (mirrors `--success-text`/`--warning-text`/`--danger-text` under `:root`). Neither fill colour clears 4.5:1 as small text — see tokens.css's own comment on the CSS declarations. */
  successText: '#117d3e',
  warningText: '#976206',
  dangerText: '#d32222',
} as const;

export const dark = {
  bgApp: '#191a23',
  bgSurface: '#21222d',
  bgElevated: '#2a2b37',
  bgInset: '#191a23',
  border: 'rgb(255 255 255 / .065)',
  borderStrong: 'rgb(255 255 255 / .13)',
  textPrimary: '#efeff4',
  textSecondary: '#a6a7b4',
  textMuted: '#8c8e9e',
  textInverse: '#0d0b0b',
  /** Mirrors the light-mode value — brand-tint backgrounds want white text in both themes. */
  textOnBrand: '#FFFFFF',
  /** Mirrors `--dot-color` under `[data-theme='dark']` in tokens.css — slightly more visible so the canvas texture reads on dark. */
  dotColor: 'rgba(237, 237, 235, 0.16)',
  /** Text-weight variants (mirrors `--success-text`/`--warning-text`/`--danger-text` under `[data-theme='dark']`). */
  successText: '#18b158',
  warningText: '#d48a09',
  dangerText: '#e97878',
} as const;

// ── Categorical chart palette ──────────────────────────────────────
//
// The six series colours, mirroring `--chart-1..6` in `tokens.css`. They were
// missing from this file entirely, which meant the only complete statement of
// the palette lived in CSS and nothing in TypeScript could assert against it.
//
// Exported as TWO explicitly-named theme objects rather than one merged
// `chart` export, on purpose. Charts render in both themes under the same
// token name, so a single flat export is an invitation to freeze one theme's
// hex into a component — the exact bug `packages/ui/.../charts/palette.ts`
// warns about in its header. Consumers that draw a chart must go through
// `CHART_SERIES` / `chartSeriesColor()`, which reference the CSS custom
// properties and therefore follow the active theme. These two objects are for
// tooling: contrast tests, palette documentation, colour-blindness checks.
//
// Measured against each theme's own `--bg-surface` (light `#ffffff`, dark
// `#161414`), WCAG 2.2 SC 1.4.11 floor of 3:1 for a meaning-carrying
// graphical mark:
//
//   light  1 7.60  2 4.17  3 6.08  4 3.39  5 5.00  6 8.78
//   dark   1 6.44  2 9.65  3 7.57  4 10.98 5 5.39  6 8.46
//
// Slots 1, 2, 3, 4 and 5 additionally carry a fixed meaning on revenue
// charts — see `apps/web/src/features/crm/widgets/revenue-categories.ts`.
export const chartLight = {
  1: '#2e6b98',
  2: '#087f91',
  3: '#12704e',
  4: '#c67a10',
  5: '#ca3c2f',
  6: '#59677d',
} as const;

export const chartDark = {
  1: '#83bce6',
  2: '#55d0df',
  3: '#1fbd83',
  4: '#f4bf76',
  5: '#da695f',
  6: '#b5bdd3',
} as const;

// ── Wingman cursor "electric" palette (v8, 2026-05-14) ────────────
//
// These three values are intentionally outside the surface palette
// because the Wingman cursor renders OUTSIDE the page chrome — over
// any background — and needs to stay vibrant regardless of the
// surrounding theme tokens. They're declared here so anything that
// references them imports through @ezhire/tokens like every other
// color in Atlas.
export const wingmanElectric = {
  /** Clicky-parity electric blue. Same value the macOS reference uses. */
  blue: '#3380FF',
  /** High-visibility Atlas teal for a cursor that floats over either theme. */
  teal: '#28C8C1',
  /** Auto pair — `white` rides on dark theme, `black` rides on light theme. */
  autoWhite: '#FFFFFF',
  autoBlack: '#0B0F19',
} as const;

// CSS gradient used as a single swatch to show users that "electric
// auto" inverts with theme. Pre-built here so the consuming component
// doesn't have to assemble hex literals inline.
export const wingmanElectricAutoSwatch =
  `linear-gradient(135deg, ${wingmanElectric.autoWhite} 50%, ${wingmanElectric.autoBlack} 50%)`;

export type AccentScale = keyof typeof accent;
export type SecondaryScale = keyof typeof secondary;
export type SurfaceToken = keyof typeof light;
